/**
 * Format: Mix and match
 * -----------------------------------------------------------------------------
 * Pure helpers for the mix & match bundle builder: active-bundle detection,
 * per-market pricing and the quantity-based discount/gift engine.
 *
 */

import productQuery from '~graphql/nodes-by-id-product-card.gql';
import { formatProduct } from '~/format/format-product';

// Line-item attributes on every box line (bundle id + unique box id).
// Keep in sync with Checkout.
export const BUNDLE_ATTRIBUTE = '_mix-n-match-bundle';
export const BUNDLE_BOX_ATTRIBUTE = '_mix-n-match-bundle-box';
export const BUNDLE_PARENT_ATTRIBUTE = '_mix-n-match-wrapper-variant';

// Set (as 'true') only on the box's free gift line, so it stays identifiable
// as a freebie at all times - e.g. by line-item.vue, without having to infer
// it from price or cross-reference the (possibly expired) bundle config.
export const BUNDLE_GIFT_ATTRIBUTE = '_mix-n-match-gift';

// URL param that re-opens a box for editing.
export const BUNDLE_BOX_PARAM = 'bundle-box';

/**
 * All variant ids referenced by any Mix & Match bundle (the parent product,
 * every selectable product, and every configured gift) - used to keep other
 * systems (e.g. the generic freebies auto-add engine, see `checkAddFreebies`
 * in the cart store) from managing a variant that Mix & Match's own reward
 * engine already owns.
 * @param {Array} bundles - `mix_and_match.bundles` array from the metafield.
 * @returns {Set<String>}
 */
export function getMixMatchVariantIds(bundles) {
  const ids = new Set();

  (bundles || []).forEach((bundle) => {
    if (bundle?.parentVariantId) {
      ids.add(String(bundle.parentVariantId));
    }

    (bundle?.productVariantSelectionId || []).forEach((id) =>
      ids.add(String(id)),
    );
    (bundle?.giftVariantIds || []).forEach((id) => ids.add(String(id)));
  });

  return ids;
}

/**
 * Finds the bundle whose parentVariantId matches variantId (compared as strings).
 * @param {Array} bundles - `mix_and_match.bundles` array from the metafield.
 * @param {String|Number} variantId - Current product variant id.
 * @returns {Object|null}
 */
export function findActiveBundle(bundles, variantId) {
  if (!Array.isArray(bundles) || variantId === null) {
    return null;
  }

  const id = String(variantId);

  return (
    bundles.find((bundle) => String(bundle?.parentVariantId) === id) || null
  );
}

/**
 * Reward unlocked at a given quantity: highest reached percentage plus any
 * active gift (only when the bundle defines gift variants).
 * @param {Object} bundle - Active bundle config.
 * @param {Number} quantity - Total quantity currently in the box.
 * @returns {{ percentage: Number, giftQuantity: Number, giftItems: Array }}
 */
export function getBundleReward(bundle, quantity) {
  const tiers = bundle?.tiers || [];
  let percentage = 0;
  let giftQuantity = 0;

  tiers.forEach((tier) => {
    if (quantity < tier.minQuantity) {
      return;
    }

    if (typeof tier.percentage === 'number') {
      percentage = Math.max(percentage, tier.percentage);
    }

    if (typeof tier.giftQuantity === 'number') {
      giftQuantity = Math.max(giftQuantity, tier.giftQuantity);
    }
  });

  const giftVariantIds = bundle?.giftVariantIds || [];
  const giftActive = giftQuantity > 0 && giftVariantIds.length > 0;

  return {
    percentage,
    giftQuantity: giftActive ? giftQuantity : 0,
    giftItems: giftActive
      ? giftVariantIds.map((variantId) => ({ variantId }))
      : [],
  };
}

/**
 * Next unreached tier (lowest minQuantity above quantity), or null when maxed
 * out. Tiers sharing a `minQuantity` (see wiki/domain/mix-and-match-bundle-builder.md)
 * are merged into one object so neither a percentage nor a gift defined there
 * gets dropped.
 * @param {Object} bundle - Active bundle config.
 * @param {Number} quantity - Total quantity currently in the box.
 * @returns {{ minQuantity: Number, percentage: Number, giftQuantity: Number }|null}
 */
export function getNextTier(bundle, quantity) {
  const tiers = bundle?.tiers || [];
  const nextMinQuantity = tiers
    .map((tier) => tier.minQuantity)
    .filter((minQuantity) => quantity < minQuantity)
    .sort((first, second) => first - second)[0];

  if (typeof nextMinQuantity !== 'number') {
    return null;
  }

  return tiers
    .filter((tier) => tier.minQuantity === nextMinQuantity)
    .reduce(
      (merged, tier) => ({
        minQuantity: nextMinQuantity,
        percentage:
          typeof tier.percentage === 'number'
            ? Math.max(merged.percentage, tier.percentage)
            : merged.percentage,
        giftQuantity:
          typeof tier.giftQuantity === 'number'
            ? Math.max(merged.giftQuantity, tier.giftQuantity)
            : merged.giftQuantity,
      }),
      { minQuantity: nextMinQuantity, percentage: 0, giftQuantity: 0 },
    );
}

/**
 * Highest-`minQuantity` *reached* gift tier — the one that unlocked the
 * active gift, kept in sync with `getBundleReward`'s highest-reached logic.
 * @param {Object} bundle - Active bundle config.
 * @param {Number} quantity - Total quantity currently in the box.
 * @returns {Object|null}
 */
export function getGiftTier(bundle, quantity) {
  return (bundle?.tiers || [])
    .filter(
      (tier) =>
        typeof tier.giftQuantity === 'number' && quantity >= tier.minQuantity,
    )
    .reduce(
      (best, tier) =>
        !best || tier.minQuantity > best.minQuantity ? tier : best,
      null,
    );
}

/**
 * Progress bar fill (0-100) towards the next tier; stays full once maxed out.
 * @param {Object} bundle - Active bundle config.
 * @param {Number} quantity - Total quantity currently in the box.
 * @returns {Number}
 */
export function getProgressPercentage(bundle, quantity) {
  const nextTier = getNextTier(bundle, quantity);

  if (!nextTier?.minQuantity) {
    const reward = getBundleReward(bundle, quantity);
    return reward.percentage > 0 || reward.giftItems.length > 0 ? 100 : 0;
  }

  return Math.min(100, (quantity / nextTier.minQuantity) * 100);
}

/**
 * Reconciles quantities against existing cart lines. A line's own quantity
 * is never sent as a "set to N" update — only what's still needed *on top*
 * of it is returned, so growth is achieved by adding the delta (Shopify
 * merges it into the existing line by variant + properties) rather than
 * updating that line in place. Also reports whether anything shrank
 * (needs fewer units than before, or disappeared entirely), since that
 * can't be achieved this way — see `findBoxShellLine`: an existing line
 * matched via `item_components` isn't independently addressable for
 * removal, only the shell line hosting it is, so shrinkage has to be
 * handled by the caller as a full remove-the-shell-then-re-add-everything
 * step instead.
 * @param {Array} lines - Existing cart lines to reconcile against.
 * @param {Object} quantities - `{ [variantId]: quantity }`.
 * @returns {{ toAdd: Object, shrunk: Boolean }} `toAdd`: leftover entries
 * still needing to be added (new variants at full quantity, grown existing
 * variants at just the delta). `shrunk`: whether any existing line needs
 * fewer units than it already has.
 */
export function reconcileBoxLines(lines, quantities) {
  const toAdd = { ...quantities };
  let shrunk = false;

  lines.forEach((line) => {
    const variantId = String(line.id);
    const delta = (toAdd[variantId] || 0) - line.quantity;

    if (delta < 0) {
      shrunk = true;
    }

    if (delta > 0) {
      toAdd[variantId] = delta;
    } else {
      delete toAdd[variantId];
    }
  });

  return { toAdd, shrunk };
}

/**
 * The Cart Transform shell line hosting this box's components, if any.
 * Distinct from `findBoxCartLines`, which returns the components
 * themselves: once merged under a shell, an individual component's own
 * `key` is display-only (surfaced via `item_components` from
 * `cart.resource.liquid`) and not an independently addressable cart
 * line — only the shell's own `key`/`id` (the actual top-level `cart.items`
 * entry) is real and can be targeted for removal.
 * @param {Array} cartItems - `cart.response.items`.
 * @param {String} boxId
 * @returns {Object|null}
 */
export function findBoxShellLine(cartItems, boxId) {
  if (!Array.isArray(cartItems) || !boxId) {
    return null;
  }

  return (
    cartItems.find((item) => {
      const components = item?.item_components;

      return (
        Array.isArray(components) &&
        components.some(
          (component) =>
            component?.properties?.[BUNDLE_BOX_ATTRIBUTE] === boxId,
        )
      );
    }) || null
  );
}

/**
 * Filters out only `hidden` variants from a product, keeping sold-out and
 * back-soon variants visible in the builder (they just can't be added to the
 * box — see the `unavailable` guard in mix-and-match-item/product-card).
 * @param {Object} product - Formatted product object.
 * @return {Object|null} - Product with hidden variants filtered out, or null
 * if no variants remain.
 */
function filterVisibleVariants(product) {
  if (!product?.variants?.length) {
    return product;
  }

  const visibleVariants = product.variants.filter(
    (variant) => variant.availableState !== 'hidden',
  );

  if (visibleVariants.length === 0) {
    return null;
  }

  return {
    ...product,
    variants: visibleVariants,
  };
}

// Max ids per Storefront request — a single request covering an entire
// large bundle's selection (100+ variants, each pulling the full productCard
// fragment) has been observed to blow Shopify's own query budget and come
// back as a 502/INTERNAL_SERVER_ERROR with no data at all. Chunking keeps
// each request cheap and lets unrelated batches still succeed if one fails.
const PRODUCT_FETCH_BATCH_SIZE = 25;

/**
 * Splits an array into fixed-size chunks.
 * @param {Array} items
 * @param {Number} size
 * @returns {Array<Array>}
 */
function chunkArray(items, size) {
  const chunks = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
}

/**
 * Fetches a single batch of nodes, swallowing (and reporting) failures so
 * one bad/oversized batch doesn't take down the others.
 * @param {Array<String>} formattedIds - Batch of `gid://…` ids.
 * @returns {Promise<Array>} - Raw nodes (may contain nulls), `[]` on failure.
 */
async function fetchProductNodeBatch(formattedIds) {
  const variables = {
    ids: formattedIds,
    country: cnvs.store.country,
    language: cnvs.store.language,
    limit: 50,
  };

  try {
    const productData = await cnvs.Query({ query: productQuery, variables });

    return productData?.nodes || [];
  } catch (error) {
    if (typeof cnvs?.ReportError === 'function') {
      cnvs.ReportError(error, { location: 'formatMixandMatch', variables });
    }

    return [];
  }
}

/**
 * Fetches and formats mix & match products. Bare ids default to
 * ProductVariant; `gid://…` strings pass through untouched. Requests are
 * chunked (see `PRODUCT_FETCH_BATCH_SIZE`) so a large bundle's selection
 * doesn't risk a single oversized, all-or-nothing Storefront request.
 * @param {Array<string|number>} ids - Variant ids or `gid://…` strings.
 * @return {Promise<Array>} - Formatted products with hidden variants filtered out.
 */
export async function formatMixandMatchProducts(ids) {
  const formattedIds = ids.map((id) =>
    String(id).startsWith('gid://')
      ? String(id)
      : `gid://shopify/ProductVariant/${id}`,
  );

  const batches = chunkArray(formattedIds, PRODUCT_FETCH_BATCH_SIZE);
  const batchResults = await Promise.all(batches.map(fetchProductNodeBatch));
  const rawNodes = batchResults.flat();

  // Nodes are Products, ProductVariants (formatProduct unwraps `.product`), or
  // Collections — flatten a Collection node down to its own product nodes,
  // since it has no product fields of its own to format.
  // Dedupe by product id — multiple requested variants/collections can share
  // a product.
  const nodes = rawNodes
    .filter(Boolean)
    .flatMap((node) =>
      node?.products?.edges
        ? node.products.edges.map((edge) => edge.node)
        : [node],
    );
  const seen = new Set();
  const products = [];

  nodes.forEach((node) => {
    let product;

    try {
      product = filterVisibleVariants(formatProduct(node));
    } catch (error) {
      if (typeof cnvs?.ReportError === 'function') {
        cnvs.ReportError(error, {
          location: 'formatMixandMatchProducts',
          node,
        });
      }

      return;
    }

    if (!product?.id || seen.has(product.id)) {
      return;
    }

    seen.add(product.id);
    products.push(product);
  });

  return products;
}

/**
 * Groups selection items by parent product, so variants of the same product
 * render as a single card. Drops items without a resolved product yet.
 * @param {Array} items - Merged selection items ({ variantId, product, variant }).
 * @returns {Array<{ productId: String, product: Object, variantIds: Array, variants: Array }>}
 */
export function groupItemsByProduct(items) {
  const groups = [];
  const groupsByProductId = {};

  (items || []).forEach((item) => {
    const productId = item?.product?.id;

    if (!productId) {
      return;
    }

    if (!groupsByProductId[productId]) {
      groupsByProductId[productId] = {
        productId,
        product: item.product,
        variantIds: [],
        variants: [],
      };
      groups.push(groupsByProductId[productId]);
    }

    groupsByProductId[productId].variantIds.push(item.variantId);
    groupsByProductId[productId].variants.push(item.variant);
  });

  return groups;
}

/**
 * Stock badge state for a group of variants — `'sold-out'`/`'back-soon'` only
 * once *every* variant is unavailable (a single-variant group is just a list
 * of one), else `null` so the flavour badge shows instead. Shared by
 * `mix-and-match-item`/`mix-and-match-product-card`'s `stockBadge` computeds.
 * @param {Array} variants
 * @returns {String|null}
 */
export function getStockBadgeState(variants) {
  const list = (variants || []).filter(Boolean);

  if (!list.length || list.some((variant) => variant.available !== false)) {
    return null;
  }

  return list.every((variant) => variant.availableState === 'back-soon')
    ? 'back-soon'
    : 'sold-out';
}

/**
 * First candidate with a real image (`.src`/`.url`) — `formatImage()` returns
 * `{}` rather than `null` when a source has no url, so a plain `||` chain
 * would stop on that empty object instead of falling through. Shared by
 * `mix-and-match-item`/`mix-and-match-product-card`'s `image` computeds,
 * each with their own candidate list.
 * @param {Array<Object|undefined>} candidates
 * @returns {Object|null}
 */
export function firstValidImage(candidates) {
  return candidates.find((image) => image?.src || image?.url) || null;
}

/**
 * Product title, using the `mix_match.custom_title` metafield when
 * `showCustomName` is on. Shared by `mix-and-match-item`/
 * `mix-and-match-product-card`'s title computeds.
 * @param {Object} product
 * @param {Boolean} showCustomName
 * @returns {String}
 */
export function resolveProductTitle(product, showCustomName) {
  if (showCustomName && product?.mixMatchCustomTitle) {
    return product.mixMatchCustomTitle;
  }

  return product?.title || '';
}

/**
 * Derives `product.options`-shaped definitions from formatted variants'
 * `selectedOptions`, for a bundle-restricted product (see `buildBundleProduct`).
 * @param {Array} variants - Formatted variants ({ selectedOptions }).
 * @returns {Array<{ name: String, values: Array, labels: Array, originalIndex: Number, index: Number }>}
 */
function buildOptionsFromVariants(variants) {
  const optionsByName = {};
  const order = [];

  (variants || []).forEach((variant) => {
    (variant?.selectedOptions || []).forEach((option) => {
      if (!optionsByName[option.name]) {
        optionsByName[option.name] = {
          name: option.name,
          values: [],
          originalIndex: option.originalIndex,
          index: option.index,
        };
        order.push(optionsByName[option.name]);
      }

      if (!optionsByName[option.name].values.includes(option.value)) {
        optionsByName[option.name].values.push(option.value);
      }
    });
  });

  return order
    .sort((first, second) => first.index - second.index)
    .map((option) => ({ ...option, labels: option.values }));
}

/**
 * Builds a product restricted to a group's own variants, so `quick-add`/
 * `product-options` never offers a variant outside the bundle.
 * @param {Object} group - Grouped selection item ({ product, variants }).
 * @returns {Object}
 */
export function buildBundleProduct(group) {
  const variants = (group?.variants || []).filter(Boolean);
  const options = buildOptionsFromVariants(variants);

  const hasOnlyDefaultVariant =
    (options.length === 1 && options[0]?.name?.toLowerCase() === 'title') ||
    (variants.length === 1 &&
      variants[0]?.title?.toLowerCase() === 'default title');

  const selectedOrFirstAvailableVariant =
    variants.find((variant) => variant.available) || variants[0];

  return {
    ...group?.product,
    hasOnlyDefaultVariant,
    options,
    selectedOrFirstAvailableVariant,
    variants,
    // `variants` above is a deliberate subset of the underlying product's
    // variants, not the page-1-of-N slice `variantsIncomplete`/
    // `variantsEndCursor` describe on a plain formatted product - without
    // this override, quick-add.vue's lazy pagination helper would see the
    // *full* product's pagination state (inherited via the spread above)
    // and "complete" this deliberately restricted list by fetching and
    // appending every other variant of the underlying product, defeating
    // the restriction this function exists for.
    variantsIncomplete: false,
    variantsEndCursor: null,
  };
}

/**
 * Resolves the product/variant ids covered by a Category Selector bubble
 * (`bundle_builder_category` metaobject), so its selectable products can be
 * intersected against the bundle's own `productVariantSelectionId`.
 * - Only one product source is expected per entry; explicit `products` wins,
 *   then `collection`, then `tag`.
 * - `tag`-sourced bubbles need no fetch — they resolve against the
 *   `pim.generic_tags` **variant** metafield on already-loaded items, so this
 *   returns `{ tag }` instead of ids (see `filterItemsByCategory`).
 * @param {Object} bubble - Category Selector metaobject entry.
 * @returns {Promise<{ ids: Array<String> }|{ tag: String }>}
 */
export async function resolveCategoryVariantIds(bubble) {
  const explicitIds = (bubble?.products || [])
    .map((product) => product?.id)
    .filter(Boolean);

  if (explicitIds.length) {
    return { ids: await resolveVariantIdsFromNodes(explicitIds) };
  }

  if (bubble?.collection?.id) {
    return { ids: await resolveVariantIdsFromNodes([bubble.collection.id]) };
  }

  if (bubble?.tag) {
    return { tag: bubble.tag };
  }

  return { ids: [] };
}

/**
 * Resolves Collection/Product/ProductVariant gids to the variant ids they
 * cover, reusing `formatMixandMatchProducts` (same query, dedupe and error
 * handling as the bundle's own selection/gift fetch).
 * @param {Array<String>} gids
 * @returns {Promise<Array<String>>}
 */
async function resolveVariantIdsFromNodes(gids) {
  const products = await formatMixandMatchProducts(gids);
  const ids = new Set();

  products.forEach((product) => {
    (product.variants || []).forEach((variant) => {
      if (typeof variant?.id !== 'undefined' && variant?.id !== null) {
        ids.add(String(variant.id));
      }
    });
  });

  return [...ids];
}

/**
 * Filters merged selection items down to those covered by a resolved
 * category. Returns `items` unchanged when there's no active category.
 * @param {Array} items - Merged selection items ({ variantId, variant }).
 * @param {{ ids: Array }|{ tag: String }|null} category - `resolveCategoryVariantIds` result.
 * @returns {Array}
 */
export function filterItemsByCategory(items, category) {
  if (!category) {
    return items || [];
  }

  if (category.tag) {
    return (items || []).filter((item) =>
      (item?.variant?.genericTags || []).includes(category.tag),
    );
  }

  const ids = new Set((category.ids || []).map(String));

  return (items || []).filter((item) => ids.has(String(item.variantId)));
}

/**
 * Filters merged selection items down to those tagged with at least one of
 * the given flavors (`mix_match.flavors` variant metafield) — multi-select,
 * OR semantics. Returns `items` unchanged when no flavor is active.
 * @param {Array} items - Merged selection items ({ variantId, variant }).
 * @param {Array<String>} flavors - Active flavor filter values.
 * @returns {Array}
 */
export function filterItemsByFlavor(items, flavors) {
  if (!flavors?.length) {
    return items || [];
  }

  return (items || []).filter((item) =>
    (item?.variant?.flavors || []).some((flavor) => flavors.includes(flavor)),
  );
}

/**
 * Unique list of flavor values across the given items, used to render the
 * filter chips. Alphabetically sorted by default; pass `order` (the
 * `bundle_builder` metaobject's `filter_order` field) to have the merchant's
 * order win instead — matched case-insensitively, any flavor value not
 * listed in `order` is appended afterwards (alphabetically), never dropped.
 * @param {Array} items - Merged selection items ({ variant }).
 * @param {Array<String>} [order] - Merchant-configured display order.
 * @returns {Array<String>}
 */
export function getAvailableFlavors(items, order) {
  const flavors = new Set();

  (items || []).forEach((item) => {
    (item?.variant?.flavors || []).forEach((flavor) => flavors.add(flavor));
  });

  if (!order?.length) {
    return [...flavors].sort((first, second) => first.localeCompare(second));
  }

  const remaining = new Set(flavors);
  const ordered = [];

  order.forEach((entry) => {
    const match = [...remaining].find(
      (flavor) => flavor.toLowerCase() === String(entry).toLowerCase(),
    );

    if (match) {
      ordered.push(match);
      remaining.delete(match);
    }
  });

  return [
    ...ordered,
    ...[...remaining].sort((first, second) => first.localeCompare(second)),
  ];
}

/**
 * Unique box id (falls back when `crypto.randomUUID` is unavailable, e.g. jsdom).
 * @returns {String}
 */
export function generateBoxId() {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `box-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Box id to edit from the URL, or null. Accepts a query string for tests.
 * @param {String} [search]
 * @returns {String|null}
 */
export function getBoxIdFromUrl(search = window?.location?.search || '') {
  return new URLSearchParams(search).get(BUNDLE_BOX_PARAM) || null;
}

/**
 * Writes or clears the box id in the URL (see `getBoxIdFromUrl`), via
 * `replaceState` so it doesn't add a history entry.
 * @param {String|null} boxId - Pass `null` to remove the param.
 */
export function setBoxIdInUrl(boxId) {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  if (boxId) {
    url.searchParams.set(BUNDLE_BOX_PARAM, boxId);
  } else {
    url.searchParams.delete(BUNDLE_BOX_PARAM);
  }

  window.history.replaceState(window.history.state, '', url);
}

/**
 * Cart lines for a box, tagged with the bundle + box id, plus each variant's
 * product/variant maxQuantity (needed for the cart store's own limit check —
 * without these it silently skips enforcing product/variant limits).
 * @param {Object} bundle - Active bundle config (needs `id`).
 * @param {Object} box - `{ [variantId]: quantity }`.
 * @param {String} boxId
 * @param {Object} [options]
 * @param {Object} [options.variantLookup] - `{ [variantId]: { product, variant } }`.
 * @param {Boolean} [options.isGift] - Tags every line with
 * `BUNDLE_GIFT_ATTRIBUTE`, for the box's free gift entries (see `saveBox` in
 * mix-and-match.vue).
 * @returns {Array<{ id: String, quantity: Number, properties: Object,
 * productId: String, productMaxQuantity: Number, variantMaxQuantity: Number }>}
 */
export function buildBoxCartLines(
  bundle,
  box,
  boxId,
  { variantLookup = {}, isGift = false } = {},
) {
  const properties = {
    [BUNDLE_ATTRIBUTE]: bundle?.id,
    [BUNDLE_PARENT_ATTRIBUTE]: bundle?.parentVariantId,
    [BUNDLE_BOX_ATTRIBUTE]: boxId,

    ...(isGift && { [BUNDLE_GIFT_ATTRIBUTE]: 'true' }),
  };

  return Object.entries(box || {})
    .filter(([, quantity]) => quantity > 0)
    .map(([variantId, quantity]) => {
      const match = variantLookup[variantId];

      return {
        id: variantId,
        quantity,
        properties: { ...properties },
        productId: match?.product?.id,
        productMaxQuantity: match?.product?.maxQuantity,
        variantMaxQuantity: match?.variant?.maxQuantity,
      };
    });
}

/**
 * Cart lines belonging to a box id.
 * - The Cart Transform Function merges every box's product lines into one
 *   "shell" line, so past that point they're no longer separate entries in
 *   `cart.items` with their own `properties` — only the shell line is. The
 *   originals only resurface as that shell's `item_components` (merged onto
 *   `cart.response.items` from `cart.resource.liquid` by `setResponse` in
 *   the cart store), each still carrying its own `properties`, so box/gift
 *   attributes survive there. Falls back to the item itself for
 *   non-transformed lines (`item_components` absent/empty).
 * @param {Array} cartItems - `cart.response.items`.
 * @param {String} boxId
 * @returns {Array<{ id: String, key: String, quantity: Number, properties: Object }>}
 */
export function findBoxCartLines(cartItems, boxId) {
  if (!Array.isArray(cartItems) || !boxId) {
    return [];
  }

  return cartItems.flatMap((item) => {
    const components = item?.item_components;

    if (Array.isArray(components) && components.length) {
      return components
        .filter(
          (component) =>
            component?.properties?.[BUNDLE_BOX_ATTRIBUTE] === boxId,
        )
        .map((component) => ({
          id: String(component.variant_id),
          key: component.key,
          quantity: component.quantity,
          properties: component.properties,
        }));
    }

    return item?.properties?.[BUNDLE_BOX_ATTRIBUTE] === boxId ? [item] : [];
  });
}

/**
 * Rebuilds box state `{ [variantId]: quantity }` from cart lines.
 * @param {Array} cartLines
 * @returns {Object}
 */
export function boxFromCartLines(cartLines) {
  return (cartLines || []).reduce((box, line) => {
    const variantId = String(line.id);
    box[variantId] = (box[variantId] || 0) + line.quantity;
    return box;
  }, {});
}
